import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import type { Theme } from '@mui/material/styles';
import { formatRate } from '@pulsepoint/shared';
import { useState } from 'react';
import { useSession, type SessionDetail } from '../../api/sessions';
import type { DialogProps } from '../../components/dialogs/DialogProvider';
import { StatusLabel } from '../../components/StatusLabel';
import { formatDateTime } from '../../lib/format';
import { colours } from '../../theme';
import { SessionChart } from './SessionChart';
import { billedTo, duration, kwhOf, whereCharged, whoCharged } from './format';

/** OCPP stop reasons, in words. */
const STOP_REASONS: Record<string, string> = {
  EVDisconnected: 'The car was unplugged',
  Local: 'Stopped at the charger',
  Remote: 'Stopped from Pulsepoint',
  DeAuthorized: 'The card was refused',
  EmergencyStop: 'Emergency stop',
  HardReset: 'The charger restarted',
  SoftReset: 'The charger restarted',
  PowerLoss: 'The charger lost power',
  Reboot: 'The charger restarted',
  UnlockCommand: 'Unlocked from Pulsepoint',
  Other: 'Other',
};

/** One session: who, where, how much, who pays, and its power over time. */
export function SessionDialog({ sessionId, close }: { sessionId: string } & DialogProps) {
  const { data: s, error } = useSession(sessionId);
  const phone = useMediaQuery((t: Theme) => t.breakpoints.down('sm'));
  return (
    <Dialog open onClose={() => close()} fullWidth maxWidth="md" fullScreen={phone} aria-labelledby="session-title">
      <DialogTitle id="session-title" component="div">
        {s ? (
          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography component="h2" sx={{ fontSize: 22, fontWeight: 500 }}>{whoCharged(s).title}</Typography>
              <Typography color="textSecondary">
                {s.chargerName} · {whereCharged(s)}
              </Typography>
            </Box>
            <Box sx={{ pt: 0.5, fontSize: 16 }}>
              {s.status === 'active' ? <StatusLabel tone="live">Charging</StatusLabel> : s.status === 'aborted' ? <StatusLabel tone="fault">Ended unexpectedly</StatusLabel> : <StatusLabel tone="neutral">Finished</StatusLabel>}
            </Box>
          </Box>
        ) : (
          <Skeleton width={240} />
        )}
      </DialogTitle>
      <DialogContent>
        {error ? <Typography color="error">{error.message}</Typography> : !s ? <Skeleton variant="rounded" height={320} /> : <Body s={s} />}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={() => close()}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

/** The price, what the energy cost (split by peak, off-peak… when it was), and any idle fee. */
function pricingFacts(s: SessionDetail): [string, string][] {
  const out: [string, string][] = [];
  const periods = s.pricing?.periods ?? [];
  if (periods.length > 1) {
    periods.forEach((p, i) => {
      const wh = s.periodEnergyWh?.[i] ?? 0;
      out.push([p.name, `${kwhOf(wh, 2)} kWh at ${formatRate(p.pricePerKwh)}`]);
    });
  } else if (s.pricePerKwh != null) {
    out.push(['Price', `${formatRate(s.pricePerKwh)} per kWh${s.pricing?.cardRate ? ' (the card’s own rate)' : ''}`]);
  }
  if (s.billingType !== 'public' && s.energyCostCents != null) out.push(['Energy cost', formatRate(s.energyCostCents / 100)]);
  if (s.idleFeeCents) out.push(['Idle fee', formatRate(s.idleFeeCents / 100)]);
  return out;
}

function Body({ s }: { s: SessionDetail }) {
  const [raw, setRaw] = useState(false);
  const who = whoCharged(s);
  const avgKw = s.durationSec > 60 ? s.energyWh / 1000 / (s.durationSec / 3600) : null;
  const peakW = Math.max(0, ...s.samples.map((x) => x.powerW ?? 0));
  const facts: [string, string][] = [
    ['Started', formatDateTime(s.startedAt)],
    ['Finished', s.stoppedAt ? formatDateTime(s.stoppedAt) : 'Still charging'],
    ['Time', duration(s.durationSec)],
    ['Energy', `${kwhOf(s.energyWh, 3)} kWh`],
    ['Average power', avgKw ? `${avgKw.toFixed(1)} kW` : '—'],
    ['Highest power', peakW ? `${(peakW / 1000).toFixed(1)} kW` : '—'],
    ['Card', who.sub || '—'],
    ['Billed to', billedTo(s)],
    ...pricingFacts(s),
    ['CO₂ saved', s.co2Kg != null ? `${s.co2Kg.toFixed(1)} kg` : '—'],
    ['Why it stopped', s.stopReason ? (STOP_REASONS[s.stopReason] ?? s.stopReason) : s.status === 'active' ? '—' : 'Not reported'],
    ['Connector', String(s.connectorId)],
    ['Meter', `${kwhOf(s.meterStartWh, 3)} → ${s.meterStopWh != null ? kwhOf(s.meterStopWh, 3) : '…'} kWh`],
    ['Session number', String(s.ocppTransactionId)],
  ];

  return (
    <Box sx={{ display: 'grid', gap: 4 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5 }}>
        <Big label="Energy" value={kwhOf(s.energyWh)} unit="kWh" />
        <Big label="Time" value={duration(s.durationSec)} />
        <Big label={s.status === 'active' ? 'Power now' : 'CO₂ saved'} value={s.status === 'active' ? (s.powerW != null ? (s.powerW / 1000).toFixed(1) : '—') : (s.co2Kg ?? 0).toFixed(1)} unit={s.status === 'active' ? 'kW' : 'kg'} />
      </Box>
      <SessionChart session={s} />
      <Box component="dl" sx={{ m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '180px 1fr' }, columnGap: 3, rowGap: { xs: 0.25, sm: 1 } }}>
        {facts.map(([k, v]) => (
          <Box key={k} sx={{ display: 'contents' }}>
            <Typography component="dt" color="textSecondary" sx={{ mt: { xs: 1, sm: 0 } }}>{k}</Typography>
            <Typography component="dd" sx={{ m: 0 }} className="num">{v}</Typography>
          </Box>
        ))}
      </Box>
      {s.rawStart !== undefined && (
        <Box>
          <Button size="small" onClick={() => setRaw(!raw)}>{raw ? 'Hide' : 'Show'} what the charger sent</Button>
          {raw && (
            <Box component="pre" sx={{ mt: 1.5, p: 2, bgcolor: colours.productGrey, borderRadius: 2, fontSize: 13, overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
              {JSON.stringify({ StartTransaction: s.rawStart, StopTransaction: s.rawStop }, null, 2)}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function Big({ label, value, unit }: { label: string; value: string; unit?: string }) {
  return (
    <Box>
      <Typography variant="body2" color="textSecondary">{label}</Typography>
      <Typography className="num" sx={{ fontSize: 32, fontWeight: 500, lineHeight: 1.15 }}>
        {value}
        {unit && <Typography component="span" sx={{ fontSize: 18, color: colours.graphite, ml: 0.5 }}>{unit}</Typography>}
      </Typography>
    </Box>
  );
}
